import { FormattedMessage } from "react-intl";
import { Box, CircularProgress, Typography } from "@mui/material";

// Centered loading / empty / error placeholder. Returns `null` when
// `status === "ready"` so callers can render it unconditionally before the chart.
export type ChartStatus = "loading" | "error" | "empty" | "ready";

// Derive the chart status from a query's flags, so every chart computes it the
// same way (loading > error > empty > ready).
export const deriveChartStatus = ({
  isLoading,
  isError = false,
  isEmpty,
}: {
  isLoading: boolean;
  isError?: boolean;
  isEmpty: boolean;
}): ChartStatus =>
  isLoading ? "loading" : isError ? "error" : isEmpty ? "empty" : "ready";

const ChartState = ({
  status,
  height,
  message,
}: {
  status: ChartStatus;
  height: number;
  // Shown when `status === "empty"` (defaults to NO_DATA). Errors always show
  // the generic SOMETHING_WENT_WRONG message in red (raw backend messages are
  // technical and untranslated).
  message?: string;
}) => {
  if (status === "ready") return null;
  return (
    <Box sx={{ display: "grid", placeItems: "center", height }}>
      {status === "loading" ? (
        <CircularProgress size={22} />
      ) : status === "error" ? (
        <Typography variant="body2" color="error">
          <FormattedMessage id="SOMETHING_WENT_WRONG" />
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {message ?? <FormattedMessage id="NO_DATA" />}
        </Typography>
      )}
    </Box>
  );
};

export default ChartState;
